import { useUserProfileCompleteProfile } from "@/lib/api/requests/user-profile.requests";
import { Button } from "@/ui-components/ui/button";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/ui-components/ui/form";
import { Input } from "@/ui-components/ui/input";
import { Card, CardContent } from "@/ui-components/ui/card";
import { zodResolver } from "@hookform/resolvers/zod";
import { User, Loader2, Shield, AlertTriangle } from "lucide-react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { toast } from "@/lib/toast";
import { useState } from "react";
import { trackMetaCustomEvent, trackMetaStandardEvent } from "@/lib/meta-pixel/meta-pixel";

export interface CompleteProfileProps {
  initialGlobalError?: string | null;
}

const CompleteProfile = ({ initialGlobalError = null }: CompleteProfileProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const [globalError, setGlobalError] = useState<string | null>(initialGlobalError);

  const formSchema = z.object({
    firstName: z.string().min(2, "Bitte gib deinen Vornamen an"),
    lastName: z.string().min(2, "Bitte gib deinen Nachnamen an"),
  });

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      firstName: "",
      lastName: "",
    },
  });

  const completeProfileMutation = useUserProfileCompleteProfile(
    undefined,
    {
      skipGlobalErrorHandler: true,
      throwOnError: true,
    }
  );

  const onSubmit = async (data: z.infer<typeof formSchema>) => {
    setIsLoading(true);
    setGlobalError(null);
    const req = {
      firstName: data.firstName,
      lastName: data.lastName,
    };
    
    completeProfileMutation.mutate(req, {
      onSuccess: () => {
        trackMetaStandardEvent("CompleteRegistration", {
          content_name: "SportyPost Registrierung",
          status: "profile_completed",
        });
        trackMetaCustomEvent("ProfileCompleted", {
          step: "complete-profile",
        });
        toast.success("Profil erfolgreich vervollständigt");
        setIsLoading(false);
        // Redirect to home or wherever appropriately
        // Since the user state changes, we might need a hard reload or just navigate
        // Navigating to / usually triggers re-check of auth
        window.location.href = "/";
      },
      onError: (error: any) => {
        toast.error("Profil konnte nicht gespeichert werden", { error });
        console.error(error);
        setIsLoading(false);
      }
    });
  };

  return (
    <div className="h-screen w-full bg-slate-50/30 flex flex-col items-center py-12 px-4 sm:px-8 overflow-y-auto">
      <div className="w-full max-w-lg space-y-10 animate-in fade-in duration-700 slide-in-from-bottom-4">
        
        {/* Header Logo */}
        <div className="flex flex-col items-center gap-6">
           <img src="/logo.png" alt="SportyPost Logo" className="h-10 md:h-12 object-contain opacity-80" />
        </div>

        {globalError && (
          <div className="w-full bg-red-50 border-l-4 border-red-500 p-4 rounded-xl shadow-sm animate-in fade-in slide-in-from-top-2">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <AlertTriangle className="h-6 w-6 text-red-500" />
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">
                  Ein Problem ist aufgetreten
                </h3>
                <div className="mt-2 text-sm text-red-700">
                  <p>{globalError}</p>
                </div>
              </div>
            </div>
          </div>
        )}

        <Card className="border-none shadow-xl shadow-slate-200/40 bg-white ring-1 ring-slate-200/60 rounded-2xl overflow-hidden">
            <CardContent className="p-0">
              <div className="p-8 md:p-10 space-y-8">
                
                <div className="flex items-start gap-4 pb-6 border-b border-slate-100">
                  <div className="p-3 bg-blue-50 rounded-xl text-blue-600 shrink-0">
                    <User className="w-6 h-6" />
                  </div>
                  <div className="space-y-1">
                    <h2 className="text-xl font-semibold text-slate-900">Profil vervollständigen</h2>
                    <p className="text-slate-500 text-sm">Bitte gib deinen Namen ein, um die Registrierung abzuschließen.</p>
                  </div>
                </div>

                <Form {...form}>
                  <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                    <FormField
                      control={form.control}
                      name="firstName"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-base font-medium text-slate-700">Vorname</FormLabel>
                          <FormControl>
                            <Input 
                              placeholder="Max" 
                              {...field} 
                              disabled={isLoading}
                              className="h-11 text-base shadow-sm border-slate-200 bg-slate-50/50 focus:bg-white transition-all focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 rounded-xl"
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="lastName"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-base font-medium text-slate-700">Nachname</FormLabel>
                          <FormControl>
                            <Input 
                              placeholder="Mustermann" 
                              {...field} 
                              disabled={isLoading}
                              className="h-11 text-base shadow-sm border-slate-200 bg-slate-50/50 focus:bg-white transition-all focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 rounded-xl"
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    
                    <div className="space-y-4 pt-4">
                        <Button 
                          type="submit" 
                          disabled={isLoading}
                          className="w-full h-11 text-base font-medium shadow-lg shadow-blue-500/25 bg-blue-600 hover:bg-blue-700 text-white rounded-xl transition-all hover:scale-[1.02] active:scale-[0.98]"
                        >
                          {isLoading ? (
                            <>
                              <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                              Speichere...
                            </>
                          ) : (
                            "Abschließen"
                          )}
                        </Button>
                    </div>
                  </form>
                </Form>

              </div>
            </CardContent>
          </Card>
          
          <div className="mt-8 flex justify-center gap-6 opacity-60 text-center text-xs font-medium text-slate-500 uppercase tracking-wider">
             <div className="flex flex-col items-center gap-2">
               <Shield className="w-5 h-5 text-slate-400" />
               <span>Sicher & DSGVO</span>
             </div>
          </div>

      </div>
    </div>
  );
};

export default CompleteProfile;
